// Event log and ingest endpoints (13 §Endpoints `events`, §Ingest contract; fix #15).
import { z } from "zod";
import { Event, EventSubjectKind, EventType } from "../events";
import { IngestBatch, IngestResponse } from "../ingest";
import { Uuid } from "../primitives";
import { CompanyItemParams, CompanyParams } from "./common";
import { defineRoute } from "./route";

const base = "/v1/companies/{co}";

/**
 * Cursor is the commit-ordered `seq`, never the event id, so a late committer is never skipped.
 * `types` repeats (`types=a&types=b`).
 */
export const EventListQuery = z.object({
  after_seq: z.coerce.number().int().min(0).optional(),
  types: z.array(EventType).max(64).optional(),
  subject_kind: EventSubjectKind.optional(),
  subject_id: Uuid.optional(),
  /** ACTIVITY: the events one run wrote (its trail in Workforce › Activity). */
  actor_run_id: Uuid.optional(),
  limit: z.coerce.number().int().min(1).max(200).optional(),
});

/** `next_cursor` is the last `seq` of the page as a decimal string; pass it as `after_seq`. */
export const EventPage = z
  .object({ data: z.array(Event), next_cursor: z.string().nullable() })
  .meta({ id: "EventPage" });

export const eventRoutes = {
  "events.list": defineRoute({
    feature: "core",
    id: "events.list",
    method: "get",
    path: `${base}/events`,
    tag: "events",
    summary: "Relayed events after a seq cursor (the polling surface for integrations)",
    access: "manager",
    params: CompanyParams,
    query: EventListQuery,
    response: { status: 200, schema: EventPage },
    errors: ["invalid_cursor"],
    emits: [],
  }),
  "events.get": defineRoute({
    feature: "core",
    id: "events.get",
    method: "get",
    path: `${base}/events/{id}`,
    tag: "events",
    summary: "One event",
    access: "manager",
    params: CompanyItemParams,
    response: { status: 200, schema: Event },
    errors: [],
    emits: [],
  }),
  "ingest.post": defineRoute({
    feature: "core",
    id: "ingest.post",
    method: "post",
    path: `${base}/ingest/events`,
    tag: "ingest",
    summary:
      "Batch of DE activity, outcome, cost and telemetry items (≤ 500 items, ≤ 1 MB; atomic)",
    description:
      "The key determines the actor; any actor field in the body is 422 actor_not_allowed. " +
      "Per-item receipts: accepted | duplicate | revised | unmapped | conflict. Items are " +
      "idempotent by item_key: a known key with the same content is duplicate (with the " +
      "original event_id), with other content conflict (nothing written for it). Batch-level " +
      "refusals (scope, kind, validation, clock skew, tz, meter_conflict) write nothing. " +
      "The LiteLLM spend puller sends its lease token as `CompanyOS-Fence`; a token that is " +
      "not the DE's live lease is 409 conflict and nothing is written (S1-12). " +
      "MONITORING-P1: `telemetry` items (scope `ingest:telemetry`) are the bridge's run and job " +
      "events; they are redacted server side, stored outside the event log (receipts without " +
      "`event_id`), and an item naming another DE's run is `rejected` (`run_not_owned`).",
    access: "ingest_key",
    params: CompanyParams,
    body: IngestBatch,
    idempotency: "required",
    response: { status: 200, schema: IngestResponse },
    errors: [
      "key_unbound",
      "scope_missing",
      "kind_not_allowed",
      "actor_not_allowed",
      "tz_mismatch",
      "meter_conflict",
      "payload_too_large",
      "conflict",
    ],
    emits: [
      "activity.recorded",
      "outcome.recorded",
      "cost.posted",
      "time_entry.created",
      "time_entry.updated",
      "time_entry.approval_changed",
      "time_entry.deleted",
    ],
  }),
} as const;
